import { getEnv } from '@/utility/env';

const host = getEnv('APP_LOGS_HOST');
const pwd = getEnv('APP_LOGS_PWD');
const env = getEnv('APP_ENV') === 'production' ? 'prod' : 'dev';

/** Regroupement des lignes proches, sans jamais retarder un envoi au-dela de MAX_DELAY. */
const DEBOUNCE = 10;
const MAX_DELAY = 500;
/** Au-dela, on envoie sans attendre : une install genere des rafales de lignes. */
const MAX_BATCH = 200;

type Options = {
    host?: string;
    labels: { app: string };
    basicAuth: string;
};

type Level = 'debug' | 'info' | 'warning' | 'error';

class Logger {
    private _timer = 0;
    private _deadline = 0;
    /**
     * Un tableau, pas un objet indexe par timestamp : deux lignes emises dans la
     * meme milliseconde s'ecrasaient mutuellement (derniere gagnante). C'est comme
     * ca que des `setup error` disparaissaient des logs, avales par le
     * `setup complete` emis juste apres.
     */
    private _logs: [string, string][] = [];
    /** Horodatage nanoseconde strictement croissant, en BigInt : au-dela de 2^53 un `number` perd des chiffres. */
    private _last = 0n;
    private readonly _options: Options;

    public constructor(options: Options) {
        this._options = options;
        this._write = this._write.bind(this);
        this.flush = this.flush.bind(this);
        if (typeof window !== 'undefined') {
            // Sans ca, tout ce qui est en attente part a la poubelle quand l'onglet
            // est ferme ou masque - typiquement juste apres une erreur.
            window.addEventListener('pagehide', this.flush);
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') this.flush();
            });
        }
    }

    public debug(msg: object) {
        this._push('debug', msg);
    }

    public info(msg: object) {
        this._push('info', msg);
    }

    public warning(msg: object) {
        this._push('warning', msg);
    }

    public error(msg: object) {
        this._push('error', msg);
    }

    /** Envoi immediat de ce qui est en attente. */
    public flush() {
        if (this._timer) clearTimeout(this._timer);
        this._write();
    }

    private _push(level: Level, msg: object) {
        const line = JSON.stringify({ level: level, ...msg });
        this._logs.push([this._timestamp(), line]);
        // Une erreur reste visible dans la console meme si l'envoi echoue.
        if (level === 'error' || level === 'warning') console.error(line);
        this._checkWrite();
    }

    private _timestamp(): string {
        const now = BigInt(Date.now()) * 1_000_000n;
        this._last = now > this._last ? now : this._last + 1n;
        return this._last.toString();
    }

    private _checkWrite() {
        if (this._logs.length >= MAX_BATCH) return this.flush();
        if (this._timer) clearTimeout(this._timer);
        if (!this._deadline) this._deadline = Date.now() + MAX_DELAY;
        const delay = Math.max(0, Math.min(DEBOUNCE, this._deadline - Date.now()));
        this._timer = setTimeout(this._write, delay);
    }

    private _write() {
        this._timer = 0;
        this._deadline = 0;
        if (this._logs.length == 0) return;
        const values = this._logs;
        this._logs = [];
        const streams = {
            streams: [{ stream: { app: `web-app-${env}` }, values: values }]
        };
        if (this._options.host) {
            fetch(this._options.host, {
                method: 'POST',
                headers: {
                    Authorization: 'Basic ' + btoa(this._options.basicAuth),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(streams),
                // Laisse la requete se terminer meme si la page se ferme.
                keepalive: true
            }).catch((err) => {
                // Les lignes perdues par le reseau restent au moins dans la console.
                console.error('logger: envoi impossible', err);
                for (const [, line] of values) console.error(line);
            });
        } else {
            for (const [, line] of values) console.log(line);
        }
    }
}

const opt: Options = {
    // host: 'https://logs.neodtx.com',
    labels: { app: `web-adb-${env}` },
    basicAuth: `studio:${pwd}`
};
if (host) opt.host = host;
if (opt.host && !opt.host?.endsWith('/loki/api/v1/push')) opt.host += '/loki/api/v1/push';

export const logger = new Logger(opt);
